import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, BookMarked, Trash2, ArrowRight, Calendar,
  LogOut, AlertTriangle, Users, Clock, CreditCard, ExternalLink,
} from 'lucide-react';
import { DPC_PRICE_PER_MONTH_SHORT, formatBillingAmount } from '../config/pricing';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  trial:      { label: 'Free trial',   tone: 'bg-amber-100 text-amber-800 border-amber-300' },
  active:     { label: 'Active',       tone: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  past_due:   { label: 'Past due',     tone: 'bg-red-100 text-red-800 border-red-300' },
  canceled:   { label: 'Canceled',     tone: 'bg-stone-200 text-stone-700 border-stone-300' },
  incomplete: { label: 'Incomplete',   tone: 'bg-amber-100 text-amber-800 border-amber-300' },
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
    });
  } catch { return '—'; }
}

function formatShortDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

/**
 * A subscription scheduled to cancel is still status="active" in Stripe, so
 * surfacing it as a plain "Active" badge is misleading — it is running, but it
 * will not renew. It gets its own state rather than being folded into either
 * "Active" or "Canceled".
 *
 * The backend normalises this into cancellation_pending, because Stripe has two
 * independent representations (the legacy cancel_at_period_end boolean and a
 * cancel_at timestamp) and a portal-initiated cancellation only sets the latter.
 * cancel_at_period_end is the fallback for records synced before that existed.
 */
function isPendingCancellation(billing) {
  return Boolean(billing?.cancellation_pending ?? billing?.cancel_at_period_end);
}

function cancellationDate(billing) {
  return billing?.cancellation_effective_at || billing?.current_period_end;
}

function resolveBillingState(billing) {
  const status = billing?.status;
  if (status === 'active' && isPendingCancellation(billing)) {
    const when = formatShortDate(cancellationDate(billing));
    return {
      label: when ? `Cancels ${when}` : 'Cancels at period end',
      shortLabel: 'Canceling',
      tone: 'bg-amber-100 text-amber-800 border-amber-300',
    };
  }
  const base = STATUS_LABELS[status] || STATUS_LABELS.trial;
  return { ...base, shortLabel: base.label };
}

function getDpcToken() {
  return localStorage.getItem('dpc_token');
}

function dpcHeaders() {
  const t = getDpcToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function trialDaysLeft(trialEndsAt) {
  if (!trialEndsAt) return 0;
  const diff = new Date(trialEndsAt) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export default function DPCDashboardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickSearch, setQuickSearch] = useState('');
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveForm, setSaveForm] = useState({ procedure_name: '', location: '' });
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    const token = getDpcToken();
    if (!token) { navigate('/dpc/login'); return; }

    // Force a reconcile against Stripe when either:
    //  - returning from checkout (?subscribed=1), so the UI reflects the payment
    //    immediately rather than waiting on webhook delivery, or
    //  - explicitly asked via ?refresh=1|true, which is the manual self-heal for
    //    an account whose webhook events were missed.
    const justSubscribed = searchParams.get('subscribed') === '1';
    const refreshParam = searchParams.get('refresh');
    const forceRefresh = refreshParam === '1' || refreshParam === 'true';
    const shouldReconcile = justSubscribed || forceRefresh;

    fetchDash(shouldReconcile, justSubscribed).then(() => {
      if (justSubscribed || refreshParam !== null) {
        searchParams.delete('subscribed');
        searchParams.delete('refresh');
        setSearchParams(searchParams, { replace: true });
      }
    });
  }, []);

  const fetchDash = async (refresh = false, announceSubscribed = false) => {
    try {
      const res = await axios.get(`${API}/dpc/dashboard`, {
        headers: dpcHeaders(),
        params: refresh ? { refresh: true } : undefined,
      });
      setDash(res.data);
      if (announceSubscribed && res.data?.subscription_status === 'active') {
        toast.success('Subscription active — thank you!');
      }
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('dpc_token');
        localStorage.removeItem('dpc_user');
        navigate('/dpc/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const res = await axios.post(
        `${API}/dpc/billing/portal`,
        { origin_url: window.location.origin },
        { headers: dpcHeaders() }
      );
      window.location.href = res.data.portal_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open the billing portal.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem('dpc_token');
    localStorage.removeItem('dpc_user');
    navigate('/dpc/login');
  };

  const handleUpgrade = async () => {
    setUpgradeLoading(true);
    try {
      const res = await axios.post(
        `${API}/dpc/subscribe`,
        { origin_url: window.location.origin },
        { headers: dpcHeaders() }
      );
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout. Please try again.');
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleQuickSearch = (e) => {
    e.preventDefault();
    if (quickSearch.trim()) {
      navigate(`/search-results?q=${encodeURIComponent(quickSearch.trim())}&source=dpc`);
    }
  };

  const handleSaveSearch = async (e) => {
    e.preventDefault();
    if (!saveForm.procedure_name.trim()) return;
    setSaveLoading(true);
    try {
      const res = await axios.post(`${API}/dpc/saved-searches`, saveForm, { headers: dpcHeaders() });
      setDash(prev => ({ ...prev, saved_searches: [res.data, ...(prev.saved_searches || [])] }));
      setSaveForm({ procedure_name: '', location: '' });
      setShowSaveForm(false);
      toast.success('Search saved.');
    } catch (e) {
      toast.error('Could not save search.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteSearch = async (id) => {
    try {
      await axios.delete(`${API}/dpc/saved-searches/${id}`, { headers: dpcHeaders() });
      setDash(prev => ({
        ...prev,
        saved_searches: prev.saved_searches.filter(s => s.id !== id),
      }));
      toast.success('Removed.');
    } catch (e) {
      toast.error('Could not remove search.');
    }
  };

  const isTrial = dash?.subscription_status === 'trial';
  const isActive = dash?.subscription_status === 'active';
  const billing = dash?.billing;
  const isCanceling = isActive && isPendingCancellation(billing);
  const cancelDate = cancellationDate(billing);
  const billingState = resolveBillingState(billing);
  const daysLeft = isTrial ? trialDaysLeft(dash?.trial_ends_at) : null;
  const searches = dash?.saved_searches ?? [];

  return (
    <div className="min-h-screen bg-[#F0EEE9]">

      {/* Header bar */}
      <div className="bg-[#0D3D4A] text-white px-6 md:px-12 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <Link to="/for-physicians" className="text-white font-semibold text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
            Meridian <span className="font-normal text-white/60">for Physicians</span>
          </Link>
          <div className="flex items-center gap-6">
            {loading ? (
              <div className="h-4 w-40 bg-white/10 animate-pulse rounded" />
            ) : (
              <span className="text-white/70 text-sm hidden md:block">
                {dash?.practice_name} &middot; Dr. {dash?.last_name}
              </span>
            )}
            {isTrial && (
              <button
                onClick={handleUpgrade}
                disabled={upgradeLoading}
                className="bg-white text-[#0D3D4A] px-4 py-2 text-xs font-semibold hover:bg-[#F0EEE9] transition-colors disabled:opacity-60 hidden sm:block"
                data-testid="upgrade-btn"
              >
                {upgradeLoading ? 'Loading…' : 'Upgrade to Pro'}
              </button>
            )}
            <button
              onClick={handleSignOut}
              className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm transition-colors"
              data-testid="dpc-signout-btn"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Trial banner */}
      {isTrial && !loading && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 md:px-12 py-3">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-amber-800">
              <AlertTriangle size={15} className="flex-shrink-0" />
              <span>
                <strong>Free trial</strong> —{' '}
                {daysLeft > 0
                  ? `${daysLeft} day${daysLeft === 1 ? '' : 's'} remaining.`
                  : 'Your trial has ended.'}{' '}
                Upgrade to continue uninterrupted access.
              </span>
            </div>
            <button
              onClick={handleUpgrade}
              disabled={upgradeLoading}
              className="bg-amber-600 text-white px-4 py-2 text-xs font-semibold hover:bg-amber-700 transition-colors disabled:opacity-60 flex-shrink-0"
              data-testid="trial-upgrade-btn"
            >
              {upgradeLoading ? 'Loading…' : `Upgrade Now — ${DPC_PRICE_PER_MONTH_SHORT}`}
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">

        {/* Page title */}
        <div className="mb-10">
          {loading ? (
            <div className="h-8 w-64 bg-stone-200 animate-pulse rounded" />
          ) : (
            <h1 className="text-3xl font-medium text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Good morning, Dr. {dash?.last_name}
            </h1>
          )}
          <p className="text-stone-500 text-sm mt-1">{dash?.practice_name}{dash?.city ? ` · ${dash.city}, ${dash?.state}` : ''}</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          {[
            {
              icon: BookMarked,
              label: 'Saved Searches',
              value: loading ? '—' : String(searches.length),
              sub: 'in your library',
            },
            {
              icon: Clock,
              label: isTrial ? 'Trial Status' : 'Subscription',
              value: loading
                ? '—'
                : isTrial
                  ? `${daysLeft}d left`
                  : billingState.shortLabel,
              sub: isTrial
                ? 'free trial'
                : isCanceling
                  ? `ends ${formatDate(cancelDate)}`
                  : isActive
                    ? 'monthly plan'
                    : 'needs attention',
            },
            {
              icon: Users,
              label: 'Referrals Generated',
              value: loading ? '—' : String(dash?.referral_count ?? 0),
              sub: 'all time',
            },
          ].map((card, i) => (
            <div key={i} className="bg-white border border-stone-200 p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 bg-[#0D3D4A]/10 flex items-center justify-center">
                  <card.icon size={16} className="text-[#0D3D4A]" />
                </div>
                <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">{card.label}</span>
              </div>
              <p className="text-3xl font-bold text-stone-900 mb-0.5">{card.value}</p>
              <p className="text-xs text-[#A8B4B8]">{card.sub}</p>
            </div>
          ))}
        </div>

        {/* Account & Billing */}
        <div className="bg-white border border-stone-200 p-8 mb-10" data-testid="billing-section">
          <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
            <div>
              <h2
                className="text-xl font-semibold text-stone-900 mb-1"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Account &amp; Billing
              </h2>
              <p className="text-stone-400 text-sm">
                Manage your subscription, payment method, and invoices.
              </p>
            </div>
            {!loading && (
              <span
                className={`text-xs font-semibold px-3 py-1.5 border ${billingState.tone}`}
                data-testid="billing-status-badge"
              >
                {billingState.label}
              </span>
            )}
          </div>

          {loading ? (
            <div className="space-y-3">
              <div className="h-4 bg-stone-100 rounded w-1/3 animate-pulse" />
              <div className="h-4 bg-stone-100 rounded w-1/2 animate-pulse" />
            </div>
          ) : (
            <>
              <dl className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-7">
                <div>
                  <dt className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-1.5">Plan</dt>
                  <dd className="text-sm text-stone-800 font-medium">
                    {billing?.plan_name || 'Meridian for Physicians'}
                  </dd>
                </div>

                {isActive ? (
                  <>
                    <div>
                      <dt className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-1.5">
                        {isCanceling ? 'Service ends' : 'Next billing date'}
                      </dt>
                      <dd className="text-sm text-stone-800 font-medium">
                        {formatDate(isCanceling ? cancelDate : billing?.current_period_end)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-1.5">
                        {isCanceling ? 'Renewal' : 'Amount'}
                      </dt>
                      <dd className="text-sm text-stone-800 font-medium">
                        {isCanceling
                          ? 'Will not renew — no further charges'
                          : `${formatBillingAmount(billing?.amount, billing?.currency)}${
                              billing?.interval ? ` / ${billing.interval}` : ''
                            }`}
                      </dd>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <dt className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-1.5">
                        Trial ends
                      </dt>
                      <dd className="text-sm text-stone-800 font-medium">
                        {formatDate(billing?.trial_ends_at)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-1.5">
                        After trial
                      </dt>
                      <dd className="text-sm text-stone-800 font-medium">
                        {billing?.price_display || DPC_PRICE_PER_MONTH_SHORT} / month
                      </dd>
                    </div>
                  </>
                )}
              </dl>

              {billing?.status === 'past_due' && (
                <div className="bg-red-50 border border-red-200 p-3 text-sm text-red-700 mb-5">
                  Your last payment failed. Update your payment method to restore access.
                </div>
              )}

              {isCanceling && (
                <div
                  className="bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 mb-5"
                  data-testid="canceling-notice"
                >
                  Your subscription is scheduled to cancel on{' '}
                  <strong>{formatDate(cancelDate)}</strong>. You keep full
                  access until then, and you will not be charged again. Use Manage Billing to
                  resume your subscription.
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                {billing?.has_stripe_customer && (
                  <button
                    onClick={handleManageBilling}
                    disabled={portalLoading}
                    className="flex items-center gap-2 border border-[#0D3D4A] text-[#0D3D4A] px-5 py-2.5 text-sm font-medium hover:bg-[#0D3D4A] hover:text-white transition-colors disabled:opacity-60"
                    data-testid="manage-billing-btn"
                  >
                    <CreditCard size={15} />
                    {portalLoading ? 'Opening…' : 'Manage Billing'}
                    <ExternalLink size={13} />
                  </button>
                )}
                {!isActive && (
                  <button
                    onClick={handleUpgrade}
                    disabled={upgradeLoading}
                    className="bg-[#0D3D4A] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#0E4557] transition-colors disabled:opacity-60"
                    data-testid="billing-upgrade-btn"
                  >
                    {upgradeLoading ? 'Loading…' : `Subscribe — ${DPC_PRICE_PER_MONTH_SHORT}`}
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Quick Search — Primary Workflow */}
        <div className="bg-white border border-stone-200 p-8 mb-10">
          <h2
            className="text-xl font-semibold text-stone-900 mb-1"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Search Procedure Prices
          </h2>
          <p className="text-stone-400 text-sm mb-5">
            Type a procedure, imaging type, or CPT code with location — e.g. "MRI lumbar spine Dallas TX"
          </p>
          <form onSubmit={handleQuickSearch} className="flex gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8B4B8]" />
              <input
                type="text"
                value={quickSearch}
                onChange={e => setQuickSearch(e.target.value)}
                placeholder="Search procedures or imaging by location…"
                className="w-full pl-10 pr-4 py-3 border border-[#A8B4B8] bg-[#F0EEE9] text-sm text-stone-900 focus:outline-none focus:border-[#0D3D4A] transition-colors"
                data-testid="dpc-quick-search-input"
              />
            </div>
            <button
              type="submit"
              className="bg-[#0D3D4A] text-white px-7 py-3 text-sm font-semibold hover:bg-[#0E4557] transition-colors"
              data-testid="dpc-quick-search-submit"
            >
              Search
            </button>
          </form>
        </div>

        {/* Saved Searches */}
        <div>
          <div className="flex items-center justify-between mb-5">
            <h2
              className="text-xl font-semibold text-stone-900"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Saved Searches
            </h2>
            <button
              onClick={() => setShowSaveForm(!showSaveForm)}
              className="flex items-center gap-2 text-sm font-medium text-[#0D3D4A] border border-[#0D3D4A] px-4 py-2 hover:bg-[#0D3D4A] hover:text-white transition-colors"
              data-testid="add-search-btn"
            >
              + Add Search
            </button>
          </div>

          {/* Inline add form */}
          {showSaveForm && (
            <form
              onSubmit={handleSaveSearch}
              className="bg-white border border-stone-200 p-6 mb-4 flex flex-col sm:flex-row gap-4 items-end"
              data-testid="save-search-form"
            >
              <div className="flex-1">
                <label className="block text-xs font-medium text-stone-600 mb-1">Procedure Name</label>
                <input
                  type="text"
                  value={saveForm.procedure_name}
                  onChange={e => setSaveForm({ ...saveForm, procedure_name: e.target.value })}
                  placeholder="e.g. MRI Lumbar Spine"
                  className="w-full px-3 py-2.5 border border-[#A8B4B8] text-sm focus:outline-none focus:border-[#0D3D4A]"
                  required
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-stone-600 mb-1">Location (optional)</label>
                <input
                  type="text"
                  value={saveForm.location}
                  onChange={e => setSaveForm({ ...saveForm, location: e.target.value })}
                  placeholder="e.g. Dallas TX"
                  className="w-full px-3 py-2.5 border border-[#A8B4B8] text-sm focus:outline-none focus:border-[#0D3D4A]"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={saveLoading}
                  className="bg-[#0D3D4A] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#0E4557] disabled:opacity-60"
                >
                  {saveLoading ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowSaveForm(false)}
                  className="text-stone-500 hover:text-stone-700 text-sm px-3"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-white border border-stone-200 p-6 animate-pulse">
                  <div className="h-4 bg-stone-100 rounded w-3/4 mb-3" />
                  <div className="h-3 bg-stone-100 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : searches.length === 0 ? (
            <div className="bg-white border border-stone-200 p-14 text-center">
              <BookMarked size={36} className="text-stone-200 mx-auto mb-4" />
              <p className="text-[#A8B4B8] font-medium mb-1">No saved searches yet</p>
              <p className="text-[#A8B4B8] text-sm">
                Save common searches for your patient population — no re-searching every visit.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {searches.map(s => (
                <div
                  key={s.id}
                  className="bg-white border border-stone-200 p-6 hover:border-stone-300 transition-colors"
                  data-testid={`dpc-saved-search-${s.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3
                      className="font-semibold text-stone-900 text-sm"
                      style={{ fontFamily: 'DM Sans, sans-serif' }}
                    >
                      {s.procedure_name}
                    </h3>
                    <button
                      onClick={() => handleDeleteSearch(s.id)}
                      className="text-[#A8B4B8] hover:text-red-400 transition-colors ml-2 flex-shrink-0"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {s.location && (
                    <p className="text-xs text-stone-400 mb-3">{s.location}</p>
                  )}
                  <div className="flex items-center justify-between pt-3 border-t border-stone-100">
                    <span className="flex items-center gap-1 text-xs text-[#A8B4B8]">
                      <Calendar size={11} />
                      {new Date(s.created_at).toLocaleDateString()}
                    </span>
                    <button
                      onClick={() =>
                        navigate(
                          `/search-results?q=${encodeURIComponent(
                            s.location ? `${s.procedure_name} ${s.location}` : s.procedure_name
                          )}&source=dpc`
                        )
                      }
                      className="text-xs text-[#0D3D4A] font-medium flex items-center gap-1 hover:underline"
                    >
                      Search again <ArrowRight size={11} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
